import { v4 } from '@lukeed/uuid';
import { original } from 'immer';
import cloneDeep from 'lodash/cloneDeep';
import { xFindClosestIndex } from 'ml-spectra-processing';
import { Filters, FiltersManager } from 'nmr-processing';
import { DATUM_KIND, SIGNAL_INLCUDED_KINDS, } from '../../../data/constants/signalsKinds';
import { changeRangeSignal, detectRanges, updateRangesRelativeValues, changeRangeRelativeValue, isSpectrum1D, } from '../../../data/data1d/Spectrum1D';
import { initSumOptions, setSumOptions, } from '../../../data/data1d/Spectrum1D/SumManager';
import { unlink, unlinkInAssignmentData, } from '../../../data/utilities/RangeUtilities';
import { getActiveSpectrum } from '../helper/getActiveSpectrum';
import { getSpectrum } from '../helper/getSpectrum';
import { initializeRangeViewObject, setRangesViewProperty, } from '../helper/setRangesViewProperty';
import { handleUpdateCorrelations } from './CorrelationsActions';
import { setDomain } from './DomainActions';
import { toggleDisplayingPeaks } from './PeaksActions';
import { resetSelectedTool } from './ToolsActions';
function getRangeIndex(draft, spectrumIndex, rangeID) {
    return draft.data[spectrumIndex].ranges.values.findIndex((range) => range.id === rangeID);
}
//action
function handleAutoRangesDetection(draft, action) {
    const { data, xDomain, molecules, view: { spectra: { activeTab: nucleus }, }, } = draft;
    const activeSpectrum = getActiveSpectrum(draft);
    if (activeSpectrum?.id) {
        const { index } = activeSpectrum;
        const datum = data[index];
        const [from, to] = xDomain;
        const windowFromIndex = xFindClosestIndex(datum.data.x, from);
        const windowToIndex = xFindClosestIndex(datum.data.x, to);
        // minMaxRatio default 0.05, lookNegative default false,
        const { minMaxRatio, lookNegative } = action.payload;
        const detectionOptions = {
            rangePicking: {
                integrationSum: 100,
                compile: true,
                frequencyCluster: 16,
                clean: 0.3,
                keepPeaks: true,
            },
            peakPicking: {
                broadWidth: 0.05,
                thresholdFactor: 8,
                minMaxRatio,
                direction: lookNegative ? 'both' : 'positive',
            },
        };
        detectRanges(datum, {
            ...detectionOptions,
            windowFromIndex,
            windowToIndex,
            molecules,
            nucleus,
        });
        handleUpdateCorrelations(draft);
    }
}
//action
function handleAutoSpectraRangesDetection(draft) {
    const peakPicking = {
        thresholdFactor: 8,
        minMaxRatio: 0.05,
    };
    const rangePicking = {
        integrationSum: 100,
        compile: true,
        frequencyCluster: 16,
        clean: 0.3,
        keepPeaks: true,
    };
    const { data, view: { spectra: { activeTab: nucleus }, }, molecules, } = draft;
    for (const datum of data) {
        if (datum.info.dimension === 1) {
            detectRanges(datum, {
                peakPicking,
                rangePicking,
                molecules,
                nucleus,
            });
            handleUpdateCorrelations(draft);
        }
    }
}
//action
function handleDeleteRange(draft, action) {
    const activeSpectrum = getActiveSpectrum(draft);
    if (activeSpectrum?.id) {
        const { index } = activeSpectrum;
        const { id, assignmentData, resetSelectTool = false } = action.payload;
        const datum = draft.data[index];
        if (id) {
            const rangeIndex = getRangeIndex(draft, index, id);
            unlinkInAssignmentData(assignmentData, [datum.ranges.values[rangeIndex]]);
            datum.ranges.values.splice(rangeIndex, 1);
        }
        else {
            unlinkInAssignmentData(assignmentData, datum.ranges.values);
            datum.ranges.values = [];
        }
        updateRangesRelativeValues(datum);
        handleUpdateCorrelations(draft);
        if (resetSelectTool) {
            resetSelectedTool(draft);
        }
    }
}
//action
function handleChangeRangeSignalKind(draft, action) {
    const state = original(draft);
    const activeSpectrum = getActiveSpectrum(state);
    if (activeSpectrum?.id) {
        const { index } = activeSpectrum;
        const { range, kind } = action.payload;
        const rangeIndex = getRangeIndex(state, index, range.id);
        const _range = draft.data[index].ranges.values[rangeIndex];
        if (_range?.signals) {
            _range.signals[range.tableMetaInfo.signalIndex].kind = kind;
            _range.kind = SIGNAL_INLCUDED_KINDS.includes(kind)
                ? DATUM_KIND.signal
                : DATUM_KIND.mixed;
            updateRangesRelativeValues(draft.data[index]);
            handleUpdateCorrelations(draft);
        }
    }
}
//action
function handleSaveEditedRange(draft, action) {
    const state = original(draft);
    const activeSpectrum = getActiveSpectrum(state);
    if (activeSpectrum?.id) {
        const { index } = activeSpectrum;
        const { range, assignmentData } = action.payload;
        // remove assignments in global state
        const _editedRowData = unlink(range);
        delete _editedRowData.tableMetaInfo;
        delete _editedRowData.rowKey;
        // remove assignments in assignment hook data
        // for now: clear all assignments for this range because signals or levels to store might have changed
        unlinkInAssignmentData(assignmentData, [_editedRowData]);
        const rangeIndex = getRangeIndex(state, index, _editedRowData.id);
        if (_editedRowData.id === 'new') {
            _editedRowData.id = v4();
        }
        draft.data[index].ranges.values.splice(rangeIndex, 1, _editedRowData);
        updateRangesRelativeValues(draft.data[index]);
        handleUpdateCorrelations(draft);
        resetSelectedTool(draft);
    }
}
function deleteSignal1D(draft, props) {
    const { spectrum, range, signal, assignmentData } = props;
    const datum1D = draft.data.find((datum) => datum.id === spectrum.id);
    const rangeIndex = datum1D.ranges.values.findIndex((_range) => _range.id === range.id);
    const signalIndex = range.signals.findIndex((_signal) => _signal.id === signal.id);
    // remove assignments for the signal range object in global state
    const _range = unlink(cloneDeep(range), {
        unlinkType: 'signal',
        signalIndex,
    });
    unlinkInAssignmentData(assignmentData, [{ signals: [signal] }]);
    _range.signals.splice(signalIndex, 1);
    datum1D.ranges.values[rangeIndex] = _range;
    // if no signals are existing in a range anymore then delete this range
    if (_range.signals.length === 0) {
        unlinkInAssignmentData(assignmentData, [_range]);
        datum1D.ranges.values.splice(rangeIndex, 1);
    }
    handleUpdateCorrelations(draft);
}
//action
function handleDeleteSignal(draft, action) {
    deleteSignal1D(draft, action.payload);
}
function unlinkRange(draft, data) {
    const activeSpectrum = getActiveSpectrum(draft);
    if (activeSpectrum?.id) {
        const { index } = activeSpectrum;
        const { assignmentData, range: rangeData, signalIndex = -1 } = data;
        // remove assignments in global state
        if (rangeData) {
            const rangeIndex = getRangeIndex(draft, index, rangeData.id);
            const range = cloneDeep(draft.data[index].ranges.values[rangeIndex]);
            let newRange = {};
            let id = rangeData.id;
            if (rangeData && signalIndex === -1) {
                newRange = unlink(range, { unlinkType: 'range' });
            }
            else {
                newRange = unlink(range, { unlinkType: 'signal', signalIndex });
                id = rangeData.signals[signalIndex].id;
            }
            // remove assignments in assignment hook data
            unlinkInAssignmentData(assignmentData, [
                {
                    id,
                },
            ]);
            draft.data[index].ranges.values[rangeIndex] = newRange;
        }
        else {
            const ranges = draft.data[index].ranges.values.map((range) => {
                return unlink(range);
            });
            draft.data[index].ranges.values = ranges;
            unlinkInAssignmentData(assignmentData, ranges);
        }
    }
}
//action
function handleUnlinkRange(draft, action) {
    unlinkRange(draft, action.payload);
}
//action
function handleSetDiaIDRange(draft, action) {
    const activeSpectrum = getActiveSpectrum(draft);
    if (activeSpectrum?.id) {
        const { index } = activeSpectrum;
        const { range: rangeData, diaIDs, signalIndex, nbAtoms } = action.payload;
        const getNbAtoms = (input, current = 0) => input + current;
        const rangeIndex = getRangeIndex(draft, index, rangeData.id);
        const _range = draft.data[index].ranges.values[rangeIndex];
        if (signalIndex === undefined) {
            _range.diaIDs = diaIDs;
            _range.nbAtoms = getNbAtoms(nbAtoms, _range.nbAtoms);
        }
        else {
            _range.signals[signalIndex].diaIDs = diaIDs;
            _range.signals[signalIndex].nbAtoms = getNbAtoms(nbAtoms, _range.signals[signalIndex].nbAtoms);
        }
        // _range.nbAtoms = getNbAtoms(_range);
    }
}
//action
function handleResizeRange(draft, action) {
    const spectrum = getSpectrum(draft);
    if (!spectrum)
        return;
    const { range } = action.payload;
    if (!range && !isSpectrum1D(spectrum))
        return;
    const index = spectrum.ranges.values.findIndex((i) => i.id === range.id);
    spectrum.ranges.values[index] = range;
    updateRangesRelativeValues(spectrum);
}
//action
function handleChangeRangeSum(draft, action) {
    const { options } = action.payload;
    const { data, view: { spectra: { activeTab: nucleus }, }, } = draft;
    const activeSpectrum = getActiveSpectrum(draft);
    if (activeSpectrum?.id) {
        const { index } = activeSpectrum;
        const datum = data[index];
        setSumOptions(datum.ranges, { options, nucleus });
        updateRangesRelativeValues(datum, true);
    }
}
function initiateRangeSumOptions(datum, options) {
    const { nucleus, molecules } = options;
    datum.ranges.options = initSumOptions(datum.ranges.options, {
        molecules,
        nucleus,
    });
}
//action
function handleAddRange(draft, action) {
    const { range } = action.payload;
    const { data, view: { spectra: { activeTab: nucleus }, }, molecules, } = draft;
    const activeSpectrum = getActiveSpectrum(draft);
    if (activeSpectrum?.id) {
        const { index } = activeSpectrum;
        const datum = data[index];
        datum.ranges.values.push(range);
        initiateRangeSumOptions(datum, {
            nucleus,
            molecules,
        });
        updateRangesRelativeValues(datum);
        handleUpdateCorrelations(draft);
    }
}
//action
function handleChangeRangeRelativeValue(draft, action) {
    const data = action.payload;
    const activeSpectrum = getActiveSpectrum(draft);
    if (activeSpectrum?.id) {
        const { index } = activeSpectrum;
        changeRangeRelativeValue(draft.data[index], data);
    }
}
//action
function handleChangeRangeSignalValue(draft, action) {
    const { rangeID, signalID, value } = action.payload;
    const activeSpectrum = getActiveSpectrum(draft);
    if (activeSpectrum?.id) {
        const { index } = activeSpectrum;
        const shift = changeRangeSignal(draft.data[index], {
            rangeID,
            signalID,
            newSignalValue: value,
        });
        FiltersManager.applyFilter(draft.data[index], [
            { name: Filters.shiftX.id, value: { shift } },
        ]);
        handleUpdateCorrelations(draft);
        setDomain(draft);
    }
}
//action
function handleChangeRangesSumFlag(draft) {
    const activeSpectrum = getActiveSpectrum(draft);
    if (activeSpectrum?.id) {
        const { index } = activeSpectrum;
        const options = draft.data[index].ranges.options;
        options.isSumConstant = !options.isSumConstant;
    }
}
//action
function handleUpdateRange(draft, action) {
    const { range } = action.payload;
    const activeSpectrum = getActiveSpectrum(draft);
    if (activeSpectrum?.id && range.id) {
        const datum = draft.data[activeSpectrum?.index];
        const index = datum.ranges.values.findIndex((_range) => _range.id === range.id);
        datum.ranges.values[index] = range;
    }
}
function toggleRangesViewProperty(draft, key) {
    setRangesViewProperty(draft, key, (flag) => !flag);
}
//action
function handleToggleRangesViewProperty(draft, action) {
    const { key } = action.payload;
    toggleRangesViewProperty(draft, key);
}
function handleCutRange(draft, action) {
    const { ranges: cutRanges } = action.payload;
    const spectrum = getSpectrum(draft);
    const ranges = spectrum.ranges.values;
    for (let i = 0; i < ranges.length; i++) {
        const { id } = ranges[i];
        if (cutRanges?.[id]) {
            ranges.splice(i, 1);
            ranges.push(...cutRanges[id]);
        }
    }
    updateRangesRelativeValues(spectrum);
    handleUpdateCorrelations(draft);
}
function handleChangePeaksDisplayingMode(draft) {
    toggleDisplayingPeaks(draft, 'ranges');
}
//action
function handleDeleteRangePeak(draft, action) {
    const { id } = action.payload;
    const [rangeKey, signalKey, peakKey] = id.split(',');
    const activeSpectrum = getActiveSpectrum(draft);
    if (activeSpectrum?.id) {
        const datum = draft.data[activeSpectrum?.index];
        const range = datum.ranges.values.find((range) => range.id === rangeKey);
        const signal = range?.signals.find((singla) => singla.id === signalKey);
        if (signal) {
            signal.peaks = signal.peaks?.filter((peak) => peak.id !== peakKey);
        }
    }
}
function handleChangeRangeAssignmentLabel(draft, action) {
    const { rangeID, value } = action.payload;
    const activeSpectrum = getActiveSpectrum(draft);
    if (activeSpectrum?.id) {
        const { index } = activeSpectrum;
        const spectrum = draft.data[index];
        if (!isSpectrum1D(spectrum)) {
            return;
        }
        initializeRangeViewObject(draft, activeSpectrum.id);
        const rangesView = draft.view.ranges[spectrum.id];
        if (!rangesView.showAssignmentsLabels) {
            rangesView.showAssignmentsLabels = true;
        }
        const range = spectrum.ranges.values.find((range) => range.id === rangeID);
        if (range) {
            range.assignment = value;
        }
    }
}
export { handleCutRange, handleAutoRangesDetection, handleDeleteRange, deleteSignal1D, handleDeleteSignal, handleChangeRangeSum, handleAddRange, handleResizeRange, handleChangeRangeRelativeValue, handleChangeRangeSignalValue, handleChangeRangeSignalKind, handleSaveEditedRange, handleUnlinkRange, unlinkRange, handleSetDiaIDRange, handleChangeRangesSumFlag, handleUpdateRange, handleAutoSpectraRangesDetection, handleToggleRangesViewProperty, handleChangePeaksDisplayingMode, handleDeleteRangePeak, handleChangeRangeAssignmentLabel, };
